import { ref, computed } from 'vue';

/**
 * Response interface for the country.is IP API service.
 * Contains IP and ISO country code based on the user's IP address.
 */
interface CountryIsResponse {
  ip: string;
  country: string;
}

/**
 * Stored user IP details normalized for consumers.
 */
interface UserIpDetails {
  country_code: string;
  ip: string;
}

const userIpDetails = ref<Partial<UserIpDetails>>({});
const hasFetched = ref(false);
const currentRequest = ref<Promise<Partial<UserIpDetails> | null> | null>(null);
const isLoading = computed(() => currentRequest.value !== null);

/**
 * Composable function for fetching and managing user IP details.
 * Provides reactive state and methods to retrieve geolocation information
 * based on the user's IP address, with in-memory caching and request deduplication.
 *
 * @returns An object containing:
 * - `userIpDetails` - Reactive reference to the user's IP details (partial)
 * - `isLoading` - Reactive reference indicating if a fetch is in progress
 * - `hasFetched` - Reactive reference indicating if data has been fetched at least once
 * - `initUserCountry` - Function to initialize and fetch user country data
 *
 * @example
 * ```ts
 * const { userIpDetails, isLoading, initUserCountry } = useUserIpDetails();
 * await initUserCountry();
 * console.log(userIpDetails.value.country_code);
 * ```
 */
export const useUserIpDetails = () => {
  /**
   * Fetches user country and IP details from the country.is API service.
   * Uses in-memory caching to avoid unnecessary API calls.
   *
   * @param forceRefresh - If true, bypasses cache and fetches fresh data from the API. Defaults to false.
   * @returns A promise that resolves to the IP details object or null if the fetch fails.
   *          Returns cached data if available and forceRefresh is false.
   *
   * @example
   * ```ts
   * // Fetch with cache
   * const details = await fetchUserCountry();
   *
   * // Force refresh
   * const freshDetails = await fetchUserCountry(true);
   * ```
   */
  const fetchUserCountry = async (forceRefresh = false): Promise<Partial<UserIpDetails> | null> => {
    if (!forceRefresh && Object.keys(userIpDetails.value).length > 0) {
      return userIpDetails.value;
    }

    if (isLoading.value && currentRequest.value) {
      return currentRequest.value;
    }

    currentRequest.value = (async () => {
      try {
        const response = await fetch('https://api.country.is/');

        if (!response.ok) {
          throw new Error(`Failed to fetch country: ${response.status}`);
        }

        const data: CountryIsResponse = await response.json();

        if (data?.country) {
          userIpDetails.value = { country_code: data.country, ip: data.ip };
        }

        return data || null;
      } catch {
        return null;
      } finally {
        hasFetched.value = true;
        currentRequest.value = null;
      }
    })();

    return currentRequest.value;
  };

  /**
   * Initializes the user country data by fetching it if not already fetched.
   * This is a safe initialization method that prevents duplicate requests.
   *
   * @returns A promise that resolves when initialization is complete.
   *
   * @example
   * ```ts
   * await initUserCountry();
   * // userIpDetails.value.country_code is now available
   * ```
   */
  const initUserCountry = async (): Promise<void> => {
    await fetchUserCountry();
  };

  return {
    userIpDetails,
    isLoading,
    hasFetched,
    initUserCountry,
  };
};
